import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Button, Space, Select, Tag, Modal, Form, Input, message, Row, Col, List, Typography, Empty, Popconfirm,
} from 'antd';
import {
  TeamOutlined, PlusOutlined, DeleteOutlined, UserAddOutlined, AppstoreOutlined,
} from '@ant-design/icons';
import { apiGet, apiPost, apiRequest } from '../services/api';

const { Title, Text } = Typography;

// 商务风格配色
const COLORS = {
  primary: '#1677ff',
  secondary: '#722ed1',
  success: '#52c41a',
  warning: '#faad14',
  error: '#ff4d4f',
  // 兼容旧代码的别名
  cyan: '#1677ff',
  purple: '#722ed1',
  pink: '#ff4d4f',
  green: '#52c41a',
  orange: '#faad14',
  yellow: '#faad14',
  darkBg: '#f5f5f5',
  cardBg: '#ffffff',
  borderDark: '#f0f0f0',
};

const GROUP_TYPE_OPTIONS = [
  { code: 'live_room', label: '直播间专用组', color: COLORS.primary },
  { code: 'post_interaction', label: '帖子互动组', color: COLORS.success },
  { code: 'social_follow', label: '社交关注组', color: COLORS.secondary },
  { code: 'content_production', label: '内容生产组', color: COLORS.warning },
  { code: 'custom', label: '自定义分组', color: '#6b7280' },
];

interface BotGroup {
  id: number;
  groupName: string;
  groupType: string;
  description: string;
  bindRoomId?: number;
  memberCount?: number;
  enabled: number;
  createTime: string;
}

interface GroupMember {
  id: number;
  botId: number;
  userName?: string;
  roleType?: string;
}

// 简洁页面标题组件
const PageHeader: React.FC<{ icon: React.ReactNode; title: string; subtitle?: string; color?: string }> = ({ icon, title, subtitle, color = COLORS.primary }) => (
  <div style={{ marginBottom: 24 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
      <div style={{
        width: 48, height: 48,
        background: `${color}10`,
        borderRadius: 8,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 24, color: color,
      }}>{icon}</div>
      <div>
        <Title level={2} style={{ margin: 0, fontSize: 24, fontWeight: 600 }}>{title}</Title>
        {subtitle && <Text style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: 14 }}>{subtitle}</Text>}
      </div>
    </div>
    <div style={{
      width: 120, height: 3, marginLeft: 60,
      background: `linear-gradient(90deg, ${color}, ${color}66, transparent)`,
      borderRadius: 2,
    }} />
  </div>
);

// 简洁卡片组件
const SimpleCard: React.FC<{ title?: React.ReactNode; extra?: React.ReactNode; children: React.ReactNode; style?: React.CSSProperties; accentColor?: string }> = ({ title, extra, children, style, accentColor = COLORS.primary }) => (
  <Card
    title={title ? <Text style={{ fontWeight: 600, fontSize: 16 }}>{title}</Text> : undefined}
    extra={extra}
    style={{
      background: COLORS.cardBg,
      border: `1px solid ${COLORS.borderDark}`,
      borderRadius: 12,
      overflow: 'hidden',
      ...style,
    }}
    styles={{
      header: title ? {
        borderBottom: `1px solid ${COLORS.borderDark}`,
        background: `${accentColor}08`,
      } : undefined,
      body: { padding: 24 },
    }}
  >
    {children}
  </Card>
);

const GroupManagement: React.FC = () => {
  const [groups, setGroups] = useState<BotGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<BotGroup | null>(null);
  const [members, setMembers] = useState<GroupMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [form] = Form.useForm();
  const [addMemberForm] = Form.useForm();

  const fetchGroups = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiGet<any>(`/api/bot/group`);
      setGroups(Array.isArray(data) ? data : (data?.data || []));
    } catch (e: any) {
      message.error('加载失败: ' + e.message);
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMembers = useCallback(async (groupId: number) => {
    setMembersLoading(true);
    try {
      const data = await apiGet<any>(`/api/bot/group/${groupId}/members`);
      setMembers(Array.isArray(data) ? data : (data?.data || []));
    } catch (e: any) {
      setMembers([]);
    } finally {
      setMembersLoading(false);
    }
  }, []);

  useEffect(() => { fetchGroups(); }, [fetchGroups]);
  useEffect(() => { if (selectedGroup) fetchMembers(selectedGroup.id); }, [selectedGroup, fetchMembers]);

  const handleCreateGroup = async (values: any) => {
    try {
      const data = await apiPost<any>(`/api/bot/group`, values);
      if (data.id) {
        message.success('创建成功');
        setShowCreateModal(false);
        form.resetFields();
        fetchGroups();
      }
    } catch (e: any) {
      message.error('创建失败: ' + e.message);
    }
  };

  const handleDeleteGroup = async (groupId: number) => {
    try {
      await apiRequest<any>(`/api/bot/group/${groupId}`, { method: 'DELETE' });
      message.success('删除成功');
      if (selectedGroup?.id === groupId) {
        setSelectedGroup(null);
        setMembers([]);
      }
      fetchGroups();
    } catch (e: any) {
      message.error('删除失败: ' + e.message);
    }
  };

  const handleAddMembers = async (values: any) => {
    if (!selectedGroup) return;
    const botIds = values.botIds.split(',').map((id: string) => parseInt(id.trim())).filter((id: number) => !isNaN(id));
    if (botIds.length === 0) {
      message.warning('请输入有效的机器人ID');
      return;
    }
    try {
      await apiPost<any>(`/api/bot/group/${selectedGroup.id}/members/add`, { botIds });
      message.success('添加成功');
      setShowAddMemberModal(false);
      addMemberForm.resetFields();
      fetchMembers(selectedGroup.id);
      fetchGroups();
    } catch (e: any) {
      message.error('添加失败: ' + e.message);
    }
  };

  const handleRemoveMember = async (botId: number) => {
    if (!selectedGroup) return;
    try {
      await apiPost<any>(`/api/bot/group/${selectedGroup.id}/members/remove`, { botIds: [botId] });
      message.success('移除成功');
      fetchMembers(selectedGroup.id);
      fetchGroups();
    } catch (e: any) {
      message.error('移除失败: ' + e.message);
    }
  };

  const getGroupTypeTag = (type: string) => {
    const opt = GROUP_TYPE_OPTIONS.find((o) => o.code === type);
    return (
      <Tag style={{
        background: `${opt?.color}15`,
        border: `1px solid ${opt?.color}33`,
        color: opt?.color || '#6b7280',
        borderRadius: 6,
      }}>
        {opt?.label || type}
      </Tag>
    );
  };

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto' }}>
      <PageHeader icon={<TeamOutlined />} title="分组管理" subtitle="创建和管理机器人分组" color={COLORS.error} />

      <Row gutter={24}>
        {/* 左侧：分组列表 */}
        <Col xs={24} lg={10}>
          <SimpleCard
            title={<Space><AppstoreOutlined style={{ color: COLORS.error }} /><span>分组列表</span><Tag style={{ background: `${COLORS.error}15`, border: `1px solid ${COLORS.error}33`, color: COLORS.error }}>{groups.length}</Tag></Space>}
            extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => setShowCreateModal(true)} style={{ background: COLORS.error, borderColor: COLORS.error }}>新建分组</Button>}
            accentColor={COLORS.error}
          >
            <List
              loading={loading}
              dataSource={groups}
              locale={{ emptyText: <Empty description={<Text style={{ color: '#6b7280' }}>暂无分组</Text>} /> }}
              renderItem={(group) => (
                <List.Item
                  style={{
                    cursor: 'pointer',
                    padding: '16px',
                    marginBottom: 12,
                    background: selectedGroup?.id === group.id ? `${COLORS.error}08` : COLORS.darkBg,
                    borderRadius: 12,
                    border: selectedGroup?.id === group.id ? `1px solid ${COLORS.error}` : `1px solid ${COLORS.borderDark}`,
                    transition: 'all 0.3s ease',
                  }}
                  onClick={() => setSelectedGroup(group)}
                  actions={[
                    <Popconfirm title="确定要删除此分组吗？" onConfirm={(e) => { e?.stopPropagation(); handleDeleteGroup(group.id); }} onCancel={(e) => e?.stopPropagation()}>
                      <Button type="text" danger icon={<DeleteOutlined />} onClick={(e) => e.stopPropagation()} />
                    </Popconfirm>,
                  ]}
                >
                  <List.Item.Meta
                    title={<Text style={{ fontWeight: 500 }}>{group.groupName}</Text>}
                    description={
                      <Space direction="vertical" size={4}>
                        {getGroupTypeTag(group.groupType)}
                        <Text style={{ color: '#6e7681', fontSize: 12 }}>{group.memberCount || 0} 成员</Text>
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
          </SimpleCard>
        </Col>

        {/* 右侧：成员列表 */}
        <Col xs={24} lg={14}>
          <SimpleCard
            title={
              selectedGroup ? (
                <Space><TeamOutlined style={{ color: COLORS.primary }} /><span>{selectedGroup.groupName}</span><Text style={{ color: 'rgba(0, 0, 0, 0.45)', fontWeight: 400 }}>- 成员列表</Text></Space>
              ) : <Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>请选择一个分组</Text>
            }
            extra={selectedGroup && <Button icon={<UserAddOutlined />} onClick={() => setShowAddMemberModal(true)} type="primary">添加成员</Button>}
            style={{ minHeight: 500 }}
            accentColor={COLORS.primary}
          >
            {selectedGroup ? (
              <>
                {selectedGroup.description && (
                  <Text style={{
                    color: '#8888aa',
                    display: 'block',
                    marginBottom: 16,
                    padding: '12px 16px',
                    background: COLORS.darkBg,
                    borderRadius: 10,
                    border: `1px solid ${COLORS.borderDark}`,
                  }}>{selectedGroup.description}</Text>
                )}
                <List
                  loading={membersLoading}
                  dataSource={members}
                  locale={{ emptyText: <Empty description={<Text style={{ color: '#6b7280' }}>暂无成员</Text>} /> }}
                  renderItem={(member) => (
                    <List.Item
                      style={{
                        padding: '12px 16px',
                        background: COLORS.darkBg,
                        borderRadius: 10,
                        marginBottom: 8,
                        border: `1px solid ${COLORS.borderDark}`,
                      }}
                      actions={[
                        <Popconfirm title="确定要移除此成员吗？" onConfirm={() => handleRemoveMember(member.botId)}>
                          <Button type="text" danger icon={<DeleteOutlined />} />
                        </Popconfirm>
                      ]}
                    >
                      <List.Item.Meta
                        title={<Text>{member.userName || `Bot#${member.botId}`}</Text>}
                        description={member.roleType && <Tag style={{ marginTop: 4, background: COLORS.darkBg, border: `1px solid ${COLORS.borderDark}`, color: '#8888aa' }}>{member.roleType}</Tag>}
                      />
                    </List.Item>
                  )}
                />
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: 80 }}>
                <TeamOutlined style={{ fontSize: 64, color: COLORS.borderDark, marginBottom: 20 }} />
                <Text style={{ color: '#6e7681', display: 'block', fontSize: 16 }}>请选择一个分组查看成员</Text>
              </div>
            )}
          </SimpleCard>
        </Col>
      </Row>

      {/* 创建分组弹窗 */}
      <Modal
        title={<Space><PlusOutlined style={{ color: COLORS.error }} /><span>新建分组</span></Space>}
        open={showCreateModal}
        onCancel={() => setShowCreateModal(false)}
        footer={null}
      >
        <Form form={form} onFinish={handleCreateGroup} layout="vertical" initialValues={{ groupType: 'custom' }} style={{ marginTop: 24 }}>
          <Form.Item label={<Text style={{ color: '#8888aa' }}>分组名称</Text>} name="groupName" rules={[{ required: true }]}>
            <Input placeholder="输入分组名称" />
          </Form.Item>
          <Form.Item label={<Text style={{ color: '#8888aa' }}>分组类型</Text>} name="groupType" rules={[{ required: true }]}>
            <Select options={GROUP_TYPE_OPTIONS.map((o) => ({ value: o.code, label: o.label }))} />
          </Form.Item>
          <Form.Item label={<Text style={{ color: '#8888aa' }}>描述</Text>} name="description">
            <Input.TextArea placeholder="可选" rows={3} />
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(prev, curr) => prev.groupType !== curr.groupType}>
            {({ getFieldValue }) => getFieldValue('groupType') === 'live_room' && (
              <Form.Item label={<Text style={{ color: '#8888aa' }}>绑定直播间ID</Text>} name="bindRoomId">
                <Input placeholder="输入直播间ID" />
              </Form.Item>
            )}
          </Form.Item>
          <Form.Item>
            <Space>
              <Button onClick={() => setShowCreateModal(false)}>取消</Button>
              <Button type="primary" htmlType="submit" style={{ background: COLORS.error, borderColor: COLORS.error }}>创建</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 添加成员弹窗 */}
      <Modal
        title={<Space><UserAddOutlined style={{ color: COLORS.primary }} /><span>添加成员</span></Space>}
        open={showAddMemberModal}
        onCancel={() => setShowAddMemberModal(false)}
        footer={null}
      >
        <Form form={addMemberForm} onFinish={handleAddMembers} layout="vertical" style={{ marginTop: 24 }}>
          <Form.Item label={<Text style={{ color: '#8888aa' }}>机器人ID (多个用逗号分隔)</Text>} name="botIds" rules={[{ required: true }]}>
            <Input.TextArea placeholder="例如: 1,2,3,4,5" rows={3} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button onClick={() => setShowAddMemberModal(false)}>取消</Button>
              <Button type="primary" htmlType="submit">添加</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default GroupManagement;
